import { Component, ErrorInfo, ReactNode } from 'react';
import { SourceMapConsumer } from 'source-map-js';

import { sendSlackNotification } from '../store/fetchers/utils';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override async componentDidCatch(error: Error, errorInfo: ErrorInfo): Promise<void> {
    const url = window.location.href;
    const date = new Date().toLocaleString();
    let stackTrace = error.stack || 'No stack trace available';

    if (process.env.NODE_ENV === 'production') {
      stackTrace = await this.mapErrorToOriginalSource(stackTrace);
    }

    const errorMessage = `:warning: Client-Side exception: \`${error.message}\`, stack: ${stackTrace}
    URL: ${url}, Date: ${date}`;

    sendSlackNotification({
      message: errorMessage,
      channel: 'front',
    });
  }

  // 🔥 Автоматически ищем нужный Source Map URL
  async mapErrorToOriginalSource(stackTrace: string): Promise<string> {
    try {
      // 🔹 Ищем URL минифицированного файла в стектрейсе
      const match = stackTrace.match(/(https?:\/\/[^\s)]+\.js)/);
      if (!match || !match[1]) return stackTrace;

      const jsFileUrl = match[1].replace(/:\d+:\d+$/, ''); // Убираем номера строк
      const sourceMapUrl = `${jsFileUrl}.map`;

      const response = await fetch(sourceMapUrl);

      if (!response.ok) throw new Error(`Failed to fetch source map: ${response.status}`);      

      const rawSourceMap = await response.json();
      
      const consumer = await new SourceMapConsumer(rawSourceMap);
      

      return stackTrace
        .split('\n')
        .map(line => {          
          const match = line.match(/(\d+):(\d+)/);          
          if (match) {
            const originalPos = consumer.originalPositionFor({
              line: parseInt(match[1], 10),
              column: parseInt(match[2], 10),
            });
            
            if (originalPos.source) {
              return `at ${originalPos.source}:${originalPos.line}:${originalPos.column}`;
            } else {
              return line; // Оставляем, если source не найден
            }
          }
          return line;
        })
        .join('\n');
    } catch (error) {
      return stackTrace;
    }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          <h1>Oops! Something went wrong.</h1>
          <p>We’re working on fixing the issue. Please refresh the page or try again later.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
